import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  Home as HomeIcon, Building2, Briefcase, Trash2, Bed, Sofa, Boxes,
  ArrowRight, ArrowLeft, X, Check, MapPin, AlertTriangle, Plus, Minus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZE_OPTIONS = [
  { id: "studio",   label: "Studio",      desc: "Up to 400 sqft",        icon: Bed },
  { id: "1br",      label: "1 Bedroom",   desc: "Apartment / condo",     icon: HomeIcon },
  { id: "2br",      label: "2 Bedrooms",  desc: "Small home",            icon: Sofa },
  { id: "3br",      label: "3 Bedrooms",  desc: "Mid-size home",         icon: Building2 },
  { id: "4br_plus", label: "4+ Bedrooms", desc: "Large home",            icon: Boxes },
  { id: "office",   label: "Office",      desc: "Commercial move",       icon: Briefcase },
  { id: "junk",     label: "Junk Removal",desc: "Cleanout / haul-away",  icon: Trash2 },
];

const STAIRS_OPTIONS = [
  { id: "none",       label: "None" },
  { id: "one_flight", label: "1 flight" },
  { id: "two_plus",   label: "2+ flights" },
];

export default function QuoteFlow({ step, setStep, onComplete }) {
  const [size, setSize] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);

  // ZIP service-area state: null = unchecked, true/false = result
  const [zipServed, setZipServed] = useState(null);
  const [zipChecking, setZipChecking] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    pickup_address: "",
    destination_address: "",
    zip_code: "",
    move_date: "",
    notes: "",
    stairs_pickup: "none",
    stairs_destination: "none",
    heavy_items: false,
    long_carry: false,
    estimated_distance_miles: "",
    inventory: {},
    website: "", // honeypot
  });

  // Fetch inventory defaults once (pricing display removed per request)
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/inventory/defaults`).then((inv) => {
      if (alive && inv?.data?.items) setInventoryItems(inv.data.items);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Debounced ZIP check
  useEffect(() => {
    const z = (form.zip_code || "").replace(/\D/g, "");
    if (z.length !== 5) {
      setZipServed(null);
      return;
    }
    setZipChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/service-area/check`, { params: { zip: z } });
        setZipServed(!!data.served);
      } catch {
        setZipServed(null);
      } finally {
        setZipChecking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.zip_code]);

  // Reset state when flow closes
  useEffect(() => {
    if (step === 0) {
      setZipServed(null);
    }
  }, [step]);

  const isMove = size && size !== "junk";

  const formatPhone = (raw) => {
    const d = (raw || "").replace(/\D/g, "").slice(0, 10);
    if (d.length === 0) return "";
    if (d.length < 4) return `(${d}`;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const setInvCount = (item, delta) => {
    setForm((f) => {
      const cur = Number(f.inventory[item] || 0);
      const next = Math.max(0, Math.min(99, cur + delta));
      const inv = { ...f.inventory };
      if (next === 0) delete inv[item]; else inv[item] = next;
      return { ...f, inventory: inv };
    });
  };

  const close = () => setStep(0);

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!size) { toast.error("Please pick a moving size first."); setStep(1); return; }
    if (!form.full_name || !form.email || !form.phone || !form.pickup_address) {
      toast.error("Please fill in name, email, phone, and pickup address.");
      return;
    }
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length !== 10 && !(phoneDigits.length === 11 && phoneDigits.startsWith("1"))) {
      toast.error("Please enter a valid 10-digit US phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        moving_size: size,
        estimated_distance_miles: form.estimated_distance_miles ? Number(form.estimated_distance_miles) : null,
      };
      const { data } = await axios.post(`${API}/quotes`, payload);
      toast.success(`Quote received! Reference #${data.id.slice(0, 8).toUpperCase()}`);
      setStep(4); // success step
      onComplete?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {/* ===== STEP 1: Service size selection ===== */}
      {step === 1 && (
        <motion.div
          key="size"
          initial={{ opacity: 0, scale: 0.94, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -10 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card data-testid="size-selection-card" className="w-full bg-stone-50/97 backdrop-blur-xl border border-stone-900/15 rounded-2xl shadow-[0_40px_80px_-20px_rgba(15,11,5,0.55)] overflow-hidden">
            <div className="flex items-center justify-between px-6 sm:px-7 py-4 border-b border-stone-200 bg-stone-950 text-stone-50">
              <div>
                <p className="text-[9px] uppercase tracking-[0.35em] text-amber-500 font-medium">Step 1 of 3</p>
                <h3 className="font-display text-lg sm:text-xl font-light italic mt-0.5">What are you moving?</h3>
              </div>
              <button data-testid="close-quote-btn" onClick={close} className="p-1.5 hover:bg-white/10 rounded-full">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 sm:p-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {SIZE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = size === opt.id;
                return (
                  <button
                    key={opt.id}
                    data-testid={`size-option-${opt.id}`}
                    onClick={() => {
                      setSize(opt.id);
                      setTimeout(() => setStep(2), 320);
                    }}
                    className={`relative text-left p-4 border transition-all rounded-xl ${
                      selected ? "border-amber-700 bg-amber-50" : "border-stone-300 hover:border-stone-900 bg-white"
                    }`}
                  >
                    <Icon className={`w-5 h-5 mb-2 ${selected ? "text-amber-700" : "text-stone-700"}`} />
                    <div className="font-display font-medium text-base leading-tight text-stone-950 tracking-tight">{opt.label}</div>
                    <div className="text-[11px] text-stone-500 leading-tight mt-1">{opt.desc}</div>
                    {selected && (
                      <span className="absolute top-2 right-2 w-4 h-4 bg-amber-700 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </Card>
        </motion.div>
      )}

      {/* ===== STEP 2: Contact + ZIP ===== */}
      {step === 2 && (
        <motion.div
          key="info"
          initial={{ opacity: 0, scale: 0.94, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -10 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card data-testid="personal-info-card" className="w-full bg-stone-50/97 backdrop-blur-xl border border-stone-900/15 rounded-2xl shadow-[0_40px_80px_-20px_rgba(15,11,5,0.55)] overflow-hidden">
            <div className="flex items-center justify-between px-6 sm:px-7 py-4 border-b border-stone-200 bg-stone-950 text-stone-50">
              <div>
                <p className="text-[9px] uppercase tracking-[0.35em] text-amber-500 font-medium">Step 2 of 3</p>
                <h3 className="font-display text-lg sm:text-xl font-light italic mt-0.5">Your details</h3>
              </div>
              <button onClick={() => setStep(1)} className="text-xs px-3 py-1 border border-white/30 rounded-full hover:bg-white/10 inline-flex items-center gap-1.5 tracking-wide">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
            </div>

            <div className="p-5 sm:p-6 grid grid-cols-2 gap-3.5">
              {/* Honeypot */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                <label htmlFor="website-hp">Website (leave empty)</label>
                <input id="website-hp" type="text" name="website" tabIndex={-1} autoComplete="off"
                  value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
              </div>

              <Field label="Full name" required>
                <Input data-testid="input-full-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Jane Doe" className="h-10 text-sm rounded-md border-stone-300 focus-visible:ring-amber-700" />
              </Field>
              <Field label="Phone" required>
                <Input data-testid="input-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} placeholder="(503) 867-9622" inputMode="tel" autoComplete="tel" className="h-10 text-sm rounded-md border-stone-300 focus-visible:ring-amber-700" />
              </Field>
              <Field label="Email" required full>
                <Input data-testid="input-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="h-10 text-sm rounded-md border-stone-300 focus-visible:ring-amber-700" />
              </Field>

              <Field label="Pickup ZIP" required>
                <div className="relative">
                  <Input data-testid="input-zip" required value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value.replace(/\D/g, "").slice(0, 5) })} placeholder="97201" inputMode="numeric" autoComplete="postal-code" className={`h-10 text-sm rounded-md border focus-visible:ring-amber-700 ${zipServed === false ? "border-red-400 bg-red-50" : zipServed === true ? "border-emerald-400 bg-emerald-50" : "border-stone-300"}`} />
                  {zipChecking && <span className="absolute right-2 top-3 text-[10px] text-stone-400">checking…</span>}
                  {zipServed === true && !zipChecking && <Check className="absolute right-2 top-3 w-4 h-4 text-emerald-600" />}
                  {zipServed === false && !zipChecking && <AlertTriangle className="absolute right-2 top-3 w-4 h-4 text-red-500" />}
                </div>
                {zipServed === false && (
                  <p className="text-[10px] text-red-600 mt-1 leading-tight">
                    Outside our service area (OR + SW WA). We may still help — submit and we'll review.
                  </p>
                )}
              </Field>
              <Field label="Preferred date">
                <Input data-testid="input-move-date" type="date" value={form.move_date} onChange={(e) => setForm({ ...form, move_date: e.target.value })} className="h-10 text-sm rounded-md border-stone-300 focus-visible:ring-amber-700" />
              </Field>

              <Field label="Pickup address" required full>
                <Input data-testid="input-pickup" required value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} placeholder="123 Main St, Portland, OR" className="h-10 text-sm rounded-md border-stone-300 focus-visible:ring-amber-700" />
              </Field>
              <Field label={isMove ? "Destination" : "Destination (optional for junk)"} full>
                <Input data-testid="input-destination" value={form.destination_address} onChange={(e) => setForm({ ...form, destination_address: e.target.value })} placeholder={size === "junk" ? "Optional" : "456 Oak Ave, Salem, OR"} className="h-10 text-sm rounded-md border-stone-300 focus-visible:ring-amber-700" />
              </Field>

              <div className="col-span-2 flex justify-between gap-2 pt-2">
                <Button type="button" onClick={() => setStep(1)} variant="outline" className="h-10 rounded-full text-[11px] uppercase tracking-[0.15em] px-4 border-stone-300">
                  <ArrowLeft className="w-3 h-3 mr-1.5" /> Back
                </Button>
                <Button
                  data-testid="continue-to-inventory-btn"
                  type="button"
                  onClick={() => {
                    if (!form.full_name || !form.email || !form.phone || !form.pickup_address) {
                      toast.error("Please fill name, email, phone, and pickup address.");
                      return;
                    }
                    setStep(3);
                  }}
                  className="bg-stone-950 hover:bg-amber-800 text-stone-50 h-10 rounded-full uppercase tracking-[0.2em] text-[11px] px-5 font-display transition-colors"
                >
                  <span className="inline-flex items-center gap-1.5">
                    {isMove ? "Continue" : "Review & Submit"} <ArrowRight className="w-3 h-3" />
                  </span>
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* ===== STEP 3: Inventory & special factors (moves) OR final review (junk) ===== */}
      {step === 3 && (
        <motion.div
          key="inv"
          initial={{ opacity: 0, scale: 0.94, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -10 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card data-testid="inventory-card" className="w-full bg-stone-50/97 backdrop-blur-xl border border-stone-900/15 rounded-2xl shadow-[0_40px_80px_-20px_rgba(15,11,5,0.55)] overflow-hidden">
            <div className="flex items-center justify-between px-6 sm:px-7 py-4 border-b border-stone-200 bg-stone-950 text-stone-50">
              <div>
                <p className="text-[9px] uppercase tracking-[0.35em] text-amber-500 font-medium">Step 3 of 3</p>
                <h3 className="font-display text-lg sm:text-xl font-light italic mt-0.5">
                  {isMove ? "Job details" : "Anything else?"}
                </h3>
              </div>
              <button onClick={() => setStep(2)} className="text-xs px-3 py-1 border border-white/30 rounded-full hover:bg-white/10 inline-flex items-center gap-1.5 tracking-wide">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
            </div>

            <form onSubmit={submitQuote} className="p-5 sm:p-6 space-y-4">
              {/* Special factors */}
              <div className="border border-stone-200 rounded-xl p-4 bg-white">
                <div className="text-[9px] uppercase tracking-[0.25em] font-semibold text-stone-700 mb-3">Special factors</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] text-stone-600">Stairs at pickup</Label>
                    <select data-testid="select-stairs-pickup" value={form.stairs_pickup} onChange={(e) => setForm({ ...form, stairs_pickup: e.target.value })} className="mt-1 w-full h-9 text-xs border border-stone-300 rounded-md bg-white px-2 focus:outline-none focus:ring-1 focus:ring-amber-700">
                      {STAIRS_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-stone-600">Stairs at destination</Label>
                    <select data-testid="select-stairs-dest" value={form.stairs_destination} onChange={(e) => setForm({ ...form, stairs_destination: e.target.value })} className="mt-1 w-full h-9 text-xs border border-stone-300 rounded-md bg-white px-2 focus:outline-none focus:ring-1 focus:ring-amber-700">
                      {STAIRS_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer">
                    <input type="checkbox" data-testid="checkbox-heavy" checked={form.heavy_items} onChange={(e) => setForm({ ...form, heavy_items: e.target.checked })} className="w-3.5 h-3.5 accent-amber-700" />
                    Heavy/special items (piano, safe, hot tub)
                  </label>
                  <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer">
                    <input type="checkbox" data-testid="checkbox-longcarry" checked={form.long_carry} onChange={(e) => setForm({ ...form, long_carry: e.target.checked })} className="w-3.5 h-3.5 accent-amber-700" />
                    Long carry from truck (&gt;50ft)
                  </label>
                </div>
                <div className="mt-3">
                  <Label className="text-[10px] text-stone-600">Approx. distance (miles) — optional</Label>
                  <Input data-testid="input-distance" type="number" min="0" max="3000" value={form.estimated_distance_miles} onChange={(e) => setForm({ ...form, estimated_distance_miles: e.target.value })} placeholder="e.g., 12" className="h-9 text-xs rounded-md border-stone-300 focus-visible:ring-amber-700" />
                </div>
              </div>

              {/* Inventory counts (moves only) */}
              {isMove && inventoryItems.length > 0 && (
                <div className="border border-stone-200 rounded-xl p-4 bg-white">
                  <div className="text-[9px] uppercase tracking-[0.25em] font-semibold text-stone-700 mb-3">Inventory (optional, helps accuracy)</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {inventoryItems.map((item) => {
                      const count = form.inventory[item] || 0;
                      return (
                        <div key={item} className={`flex items-center justify-between px-2.5 py-1.5 border rounded-md text-xs ${count > 0 ? "border-amber-300 bg-amber-50" : "border-stone-200"}`}>
                          <span className="truncate text-stone-700">{item}</span>
                          <div className="flex items-center gap-1 shrink-0">
                            <button type="button" data-testid={`inv-minus-${item}`} onClick={() => setInvCount(item, -1)} disabled={count === 0} className="w-5 h-5 grid place-items-center bg-stone-100 hover:bg-stone-200 rounded-sm disabled:opacity-30">
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-5 text-center font-medium tabular-nums">{count}</span>
                            <button type="button" data-testid={`inv-plus-${item}`} onClick={() => setInvCount(item, 1)} className="w-5 h-5 grid place-items-center bg-stone-900 text-stone-50 hover:bg-amber-800 rounded-sm">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="border border-stone-200 rounded-xl p-4 bg-white">
                <Label className="text-[9px] uppercase tracking-[0.25em] font-semibold text-stone-700">Additional notes</Label>
                <textarea
                  data-testid="input-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder="Anything we should know? Parking, fragile items, building rules…"
                  className="mt-2 w-full text-xs border border-stone-300 rounded-md p-2 resize-none focus:outline-none focus:ring-1 focus:ring-amber-700"
                />
              </div>

              <div className="flex justify-between pt-1">
                <Button type="button" onClick={() => setStep(2)} variant="outline" className="h-10 rounded-full text-[11px] uppercase tracking-[0.15em] px-4 border-stone-300">
                  <ArrowLeft className="w-3 h-3 mr-1.5" /> Back
                </Button>
                <Button data-testid="submit-quote-button" type="submit" disabled={submitting} className="bg-stone-950 hover:bg-amber-800 text-stone-50 h-10 rounded-full uppercase tracking-[0.2em] text-[11px] px-5 font-display transition-colors">
                  {submitting ? "Sending…" : (<span className="inline-flex items-center gap-1.5">Request Quote <ArrowRight className="w-3 h-3" /></span>)}
                </Button>
              </div>
            </form>
          </Card>
        </motion.div>
      )}

      {/* ===== STEP 4: Success ===== */}
      {step === 4 && (
        <motion.div
          key="done"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
        >
          <Card className="w-full bg-stone-50 border border-stone-900/15 rounded-2xl flex flex-col items-center justify-center text-center px-8 py-10 shadow-[0_40px_80px_-20px_rgba(15,11,5,0.55)]">
            <div className="w-12 h-12 rounded-full bg-amber-700 text-stone-50 flex items-center justify-center mb-3">
              <Check className="w-6 h-6" />
            </div>
            <h3 className="font-display text-2xl font-light italic text-stone-950 tracking-tight">You're booked in.</h3>
            <p className="text-sm text-stone-600 max-w-sm mt-2 leading-relaxed">
              A SWIFTHAUL coordinator will call you shortly to confirm the details and finalize your quote.
            </p>
            <Button
              data-testid="restart-quote-btn"
              onClick={() => {
                setSize(null);
                setForm({ ...form, full_name: "", email: "", phone: "", pickup_address: "", destination_address: "", zip_code: "", move_date: "", notes: "", inventory: {}, stairs_pickup: "none", stairs_destination: "none", heavy_items: false, long_carry: false, estimated_distance_miles: "" });
                setStep(0);
              }}
              className="mt-5 bg-stone-950 hover:bg-amber-800 text-stone-50 rounded-full h-9 px-5 text-[11px] uppercase tracking-[0.2em] font-display transition-colors"
            >
              Start over
            </Button>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-stone-700">
        {label}{required && <span className="text-amber-700"> *</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
